import Head from 'next/head'
import React, { CSSProperties, useContext } from 'react'

import { useCopy } from '@mondough/copy'
import { SkipLink } from '@mondough/monzo-ui'

import { ReferralContext } from '../../providers'
import desktopBg from '../../public/images/desktop-bg.png'
import mobileBg from '../../public/images/mobile-bg.png'
import lifestyleGeneric from '../../public/images/monzo-campaign.webp'
import lifestyleReferral from '../../public/images/referral.png'
import wideBg from '../../public/images/wide-bg.png'
import { NUMBER_OF_CUSTOMERS_MILLIONS, getCDNSrc } from '../../utils'
import styles from './Layout.module.scss'
import { Footer, Header, MultipleBlockBanner, SingleBlockBanner } from '..'

const Layout = ({
  children,
}: {
  children: React.ReactNode
  renameFlag?: boolean
}) => {
  const { referralData, defaultReferralText } = useContext(ReferralContext)
  const c = useCopy()
  const displayReferral = referralData !== null

  const headTitle = c.string({
    key: 'app_tabs_monzo_me.common.app_name',
    message: 'Monzo Split',
  })
  const description = c.string({
    key: 'app_tabs_monzo_me.common.tagline.guest_description',
    message:
      'Join Monzo Split as a guest to add, settle and track group costs.',
  })
  const url = c.string({
    key: 'app_tabs_monzo_me.default.page.url',
    message: 'https://split.monzo.me',
  })

  return (
    <div className={styles.app}>
      <Head>
        <title>{headTitle}</title>
        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1, maximum-scale=5"
        />
        <meta name="robots" content="noindex" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={headTitle} />
        <meta property="og:url" content={url} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/og-images/split-og.png" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@monzo" />
        <meta name="twitter:title" content={headTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/og-images/split-og.png" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="description" content={description} />
        <meta name="theme-color" content="#112231" />
      </Head>
      <SkipLink target="main" />
      <Header />
      <main className={styles.main} id="main">
        <div className={styles['desktop-row-container']}>
          <section
            className={styles['left-side-content']}
            style={
              {
                '--mobile-bg-url': `url(${getCDNSrc(mobileBg)})`,
                '--desktop-bg-url': `url(${getCDNSrc(desktopBg)})`,
                '--wide-bg-url': `url(${getCDNSrc(wideBg)})`,
              } as CSSProperties
            }
          >
            <div className={styles.content}>{children}</div>
          </section>
          <aside className={styles['right-side-content']}>
            <SingleBlockBanner
              title={
                displayReferral
                  ? referralData?.title
                  : c.string({
                      key: 'app_tabs_monzo_me.referral.default.title',
                      message:
                        'Join {{ numberOfMillionCustomers }} million people banking with Monzo',
                      variables: {
                        numberOfMillionCustomers: NUMBER_OF_CUSTOMERS_MILLIONS,
                      },
                    })
              }
              text={
                displayReferral
                  ? referralData?.description
                  : defaultReferralText
              }
              imageSrc={displayReferral ? lifestyleReferral : lifestyleGeneric}
              buttonText={
                displayReferral
                  ? c.string({
                      key: 'app_tabs_monzo_me.referral.claim.button',
                      message: 'Claim referral',
                    })
                  : c.string({
                      key: 'app_tabs_monzo_me.common.cta.get_free_account',
                      message: 'Get a free bank account',
                    })
              }
              buttonHref={
                displayReferral
                  ? referralData?.link
                  : 'https://monzo.com/sign-up/?utm_source=tabsmonzome&utm_medium=website'
              }
            />
          </aside>
        </div>
        <MultipleBlockBanner />
      </main>
      <Footer
        links={[
          {
            id: 'privacy-notice',
            href: 'https://monzo.com/legal/privacy-notice/',
            children: c.string({
              key: 'app_tabs_monzo_me.footer.privacy_notice',
              message: 'Privacy notice',
            }),
            analyticsName: 'footer-privacy-notice',
          },
          {
            id: 'cookies',
            href: '/cookies',
            children: c.string({
              key: 'app_tabs_monzo_me.footer.cookie_notice',
              message: 'Cookie notice',
            }),
            analyticsName: 'footer-cookie-notice',
          },
          {
            id: 't-and-c',
            href: 'https://monzo.com/legal/terms-and-conditions/',
            children: c.string({
              key: 'app_tabs_monzo_me.footer.terms_and_conditions',
              message: 'Terms & conditions',
            }),
            analyticsName: 'terms-and-conditions',
          },
        ]}
      />
    </div>
  )
}

export default Layout
